import type { Machine } from "@/features/line-machines/public";
import type { TilePos } from "@/lib/pixi/isoCoords";

/**
 * ワークの進行状態。
 *
 * 単一フロー＋バッファ仕様:
 *   pre_press → pressing → post_press → in_buffer → being_picked → on_transport → unloading → done
 *
 * - in_buffer: コンベア終端の仮置きバッファ内（bufferSlot で位置を保持）
 *   旧仕様の "await_pickup" は in_buffer slot 0 に相当
 *
 * 将来 "defect_rework" や "defect_rejected" などを追加する想定。
 */
export type WorkPhase =
  | "pre_press"
  | "pressing"
  | "post_press"
  | "in_buffer"
  | "being_picked"
  | "on_transport"
  | "unloading"
  | "done";

/**
 * ワークの状態。
 *
 * 拡張用フィールド（現状は初期値のみ設定、挙動には未反映）:
 * - `defectStatus`: 将来の不良品発生機能で使用
 * - `dwellTime`: 滞留時間の計測（将来の対流表現・カウント機能で使用）
 */
export interface Work {
  id: string;
  phase: WorkPhase;
  /** 現在乗っているコンベアの id (P3: マルチコンベア対応) */
  conveyorId: string;
  /** そのコンベアの path 上の連続位置（タイル単位、0 が始点、path.length-1 が終点） */
  pathDist: number;
  /** 現フェーズ経過秒数（pressing / being_picked / unloading で使用） */
  phaseTimer: number;
  /** 将来用: 品質ステータス。v1 は常に "ok" */
  defectStatus: "ok" | "defect";
  /** 将来用: 現サイクルで待機・滞留した累計秒数 */
  dwellTime: number;
  /** 直近に停止し始めてからの経過秒数。前進したら 0 にリセット。自動停止判定に使用 */
  currentStallSec: number;
  /** in_buffer 中のスロット番号（0 = 最も右 = アーム寄り）。それ以外は null */
  bufferSlot: number | null;
  /** シミュレーション内の生成時刻（秒） */
  createdAt: number;
}

export type ArmPhase = "idle" | "picking" | "placing";

export interface RobotArm {
  /** 対応する scene の ArmObject.id */
  id: string;
  position: TilePos;
  phase: ArmPhase;
  timer: number;
  heldWorkId: string | null;
  /** 根元からの回転角（ラジアン）— 描画用 */
  armAngle: number;
}

export type TransportPhase = "idle_at_line" | "to_unload" | "unloading" | "returning";

export interface TransportRobot {
  /** 対応する scene の TransportObject.id */
  id: string;
  phase: TransportPhase;
  /** タイル座標（小数）。描画で等角変換する */
  position: { col: number; row: number };
  /** 次に向かうウェイポイントのインデックス */
  waypointIndex: number;
  /** unloading 中の経過秒数（フェードアウト進捗の元） */
  unloadTimer: number;
  /** 積載中のワーク ID 配列（最大 TRANSPORT_CAPACITY、ロード順） */
  cargo: string[];
}

/** ワーク投入モード（C1 — 3種切替） */
export type SpawnMode = { kind: "auto"; intervalSec: number } | { kind: "manual" } | { kind: "queued"; maxN: number };

/** ライブに可変なパラメータ（UIと同期、稼働中も即時反映） */
export interface SimulationParams {
  conveyorSpeed: number;
  pressProcessingTime: number;
  armTime: number;
  transportSpeed: number;
}

/**
 * 系統別の稼働可否フラグ。
 * `false` の系統は tick 内で即時停止 (進行中の動作も次フレームで止まる)。
 * 主に network 層から導出されるが、エンジン自体は network 概念を知らない。
 */
export interface SimPermissions {
  pressEnabled: boolean;
  armEnabled: boolean;
  transportEnabled: boolean;
  spawnEnabled: boolean;
}

/** すべて許可されたデフォルト権限（既存テスト・初期状態用） */
export const ALL_SIM_PERMISSIONS_ENABLED: SimPermissions = {
  pressEnabled: true,
  armEnabled: true,
  transportEnabled: true,
  spawnEnabled: true,
};

/** tick の追加引数。将来センサ/MES 等が増えてもここに足す。 */
export interface SimulationContext {
  permissions: SimPermissions;
}

/** 既定の SimulationContext（全許可） */
export const DEFAULT_SIM_CONTEXT: SimulationContext = {
  permissions: ALL_SIM_PERMISSIONS_ENABLED,
};

/**
 * UI スライダ用の単純化されたレイアウト記述 (旧仕様の互換ビュー)。
 * 真の配置情報は `FactoryScene` (scene-types.ts) に持ち、
 * `deriveLayoutFromScene` で本構造を派生させる。
 *
 * - conveyorLength: 第 1 コンベアの path 長
 * - pressPosition : 第 1 プレスの col 位置
 * - bufferCapacity: 第 1 コンベアのバッファ容量
 * - transportCapacity: 第 1 搬送ロボの積載上限
 */
export interface Layout {
  conveyorLength: number;
  pressPosition: number;
  bufferCapacity: number;
  transportCapacity: number;
}

/** 統計 */
export interface SimulationStats {
  completedCount: number;
  lastCycleTimeSec: number | null;
  /** 直近60秒以内に完成したワーク数から算出 */
  throughputPerMin: number;
  /** 直近完成時刻のリスト（スループット算出用） */
  recentCompletionTimes: number[];
}

/** エンジンが保持する全状態 */
export interface SimulationState {
  /** シミュレーション内経過時間（秒） */
  time: number;
  /**
   * 配置情報の真の保持先（FactoryScene）。エディタ経由の変更はここを更新し、
   * reset で再構築される。
   */
  scene: import("@/features/simulation/scene-types").FactoryScene;
  /** Scene の第 1 オブジェクトから派生する旧式レイアウトビュー (互換用) */
  layout: Layout;
  works: Work[];
  /** scene.presses から構築。各 Press の動作状態 (busy / progress 等) */
  machines: Machine[];
  /** scene.arms から構築。各アームの動作状態 (P3) */
  arms: RobotArm[];
  /** scene.transports から構築。各搬送ロボの動作状態 (P3) */
  transports: TransportRobot[];
  /** arms[0] への別名 (旧式テスト互換、同一参照) */
  arm: RobotArm;
  /** transports[0] への別名 (旧式テスト互換、同一参照) */
  transport: TransportRobot;
  stats: SimulationStats;
  spawnMode: SpawnMode;
  /** auto モードの次スポーンまでのタイマー */
  spawnTimer: number;
  /** queued モードの待ち行列 */
  queuedSpawns: number;
  /** 連番IDの種 */
  nextWorkId: number;
  /**
   * コンベア自動停止フラグ。
   * ワークが一定時間滞留したときに true にセットされ、ベルト上のワーク移動と新規投入を停止する。
   * プレス / アーム / 搬送ロボットはこの間も継続動作する。
   * ユーザーが resumeConveyor() を呼ぶと false に戻る。
   */
  conveyorStopped: boolean;
  /** 自動停止した時刻（UI 表示用）。停止中でなければ null */
  conveyorStoppedAt: number | null;
}
